import React, { useEffect, memo, useCallback } from "react";
import BackgroundImage from "../../../../../components/ui/BackgroundImage";
import parentsBg from "../../../assets/images/parent-bg.png";
import Field from "../../ui/Field";
import { FATHERS, MOMS } from "../../../constants/parents";
import { Parent } from "../../../character.types";
import { motion, AnimatePresence } from "framer-motion";

interface Props {
  className?: string;
  label?: string;
  description?: string;
  selectedMother: Parent;
  selectedFather: Parent;
  onMotherChange: (mom: Parent) => void;
  onFatherChange: (father: Parent) => void;
}

// ─── Header (UNCHANGED) ────────────────────────────────────────────────────────
const headerItem: any = {
  hidden: { opacity: 0, y: -5 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.28, delay: i * 0.07, ease: "easeOut" },
  }),
};

// ─── Brutal image swap: slam up from below, flash-exit upward ─────────────────
const parentImgVariants: any = {
  enter: {
    opacity: 0,
    y: 60,
    scale: 0.76,
    filter: "brightness(0) blur(4px)",
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    filter: "brightness(1) blur(0px)",
    transition: {
      opacity: { duration: 0.22, ease: "easeOut" },
      y: { type: "spring", stiffness: 500, damping: 24, mass: 0.6 },
      scale: { type: "spring", stiffness: 440, damping: 21, mass: 0.6 },
      filter: { duration: 0.32, ease: "easeOut" },
    },
  },
  exit: {
    opacity: 0,
    y: -32,
    scale: 0.88,
    filter: "brightness(3) blur(3px)",
    transition: {
      duration: 0.1,
      ease: [0.6, 0, 1, 0.4],
    },
  },
};

// ─── Slot initial reveal: staggered fade-in-up on mount ───────────────────────
const slotVariants: any = {
  hidden: { opacity: 0, y: 20 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.38,
      delay: 0.08 + i * 0.14,
      ease: [0.22, 1, 0.36, 1],
    },
  }),
};

const ParentsSelector: React.FC<Props> = memo(
  ({
    className,
    label,
    description,
    selectedMother,
    selectedFather,
    onMotherChange,
    onFatherChange,
  }) => {
    const includesHeader = label !== undefined || description !== undefined;

    const handleMotherChange = useCallback(
      (value: unknown) => onMotherChange(value as Parent),
      [onMotherChange],
    );

    const handleFatherChange = useCallback(
      (value: unknown) => onFatherChange(value as Parent),
      [onFatherChange],
    );

    useEffect(() => {
      [...MOMS, ...FATHERS].forEach((parent) => {
        const img = new Image();
        img.src = parent.imageSrc;
      });
    }, []);

    return (
      <div className={`${className}`}>
        {includesHeader && (
          <div className="mb-2">
            {label && (
              <motion.h2
                className="text-white leading-6 text-[1.25rem] font-medium"
                custom={0}
                variants={headerItem}
                initial="hidden"
                animate="visible"
              >
                {label}
              </motion.h2>
            )}
            {description && (
              <motion.p
                className="text-white/50 text-[.9rem]"
                custom={1}
                variants={headerItem}
                initial="hidden"
                animate="visible"
              >
                {description}
              </motion.p>
            )}
          </div>
        )}

        <BackgroundImage
          imageSrc={parentsBg}
          imageClassName="!object-fill rounded-md"
        >
          <div className="p-5 pb-0 grid grid-cols-2 gap-2 overflow-hidden">
            {/* ── Mom ─────────────────────────────────────────────────────────── */}
            <motion.div
              className="relative min-h-[170px] 1440px:min-h-[235px] pb-5 flex flex-col justify-end"
              custom={0}
              variants={slotVariants}
              initial="hidden"
              animate="visible"
            >
              <AnimatePresence mode="wait">
                <div className="absolute bottom-0 left-0 right-0 w-full h-full">
                  <motion.img
                    key={selectedMother.imageSrc}
                    src={selectedMother.imageSrc}
                    alt="Mother"
                    loading="eager"
                    className="object-contain w-full h-full"
                    variants={parentImgVariants}
                    initial="enter"
                    animate="visible"
                    exit="exit"
                    style={{ willChange: "transform, opacity, filter" }}
                  />
                </div>
              </AnimatePresence>
              <Field
                type="select"
                options={MOMS}
                initialValue={selectedMother}
                onChange={handleMotherChange}
              />
            </motion.div>

            {/* ── Father ──────────────────────────────────────────────────────── */}
            <motion.div
              className="relative min-h-[170px] 1440px:min-h-[235px] pb-5 flex flex-col justify-end"
              custom={1}
              variants={slotVariants}
              initial="hidden"
              animate="visible"
            >
              <AnimatePresence mode="wait">
                <div className="absolute bottom-0 left-0 right-0 w-full h-full">
                  <motion.img
                    key={selectedFather.imageSrc}
                    src={selectedFather.imageSrc}
                    alt="Father"
                    loading="eager"
                    className="object-contain w-full h-full"
                    variants={parentImgVariants}
                    initial="enter"
                    animate="visible"
                    exit="exit"
                    style={{ willChange: "transform, opacity, filter" }}
                  />
                </div>
              </AnimatePresence>
              <Field
                type="select"
                options={FATHERS}
                initialValue={selectedFather}
                onChange={handleFatherChange}
              />
            </motion.div>
          </div>
        </BackgroundImage>
      </div>
    );
  },
);

export default memo(ParentsSelector);
